import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProductDto } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { credits } from "../lib/format";
import { creditPackageImageUrl } from "../lib/credits";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { ProductImage, ProductPrice, productImageSrc } from "../components/ProductCard";
import { Badge, Button, PageLoader } from "../components/ui";
import { FlexxiShopPanel } from "../components/shop/FlexxiShopPanel";
import { isFlexxiShopProduct } from "../lib/flexxiShop";
import { WishlistHeart } from "../components/wishlist/WishlistHeart";
import { ProductReviewsPanel } from "../components/reviews/ProductReviewsPanel";
import { StarRating } from "../components/reviews/StarRating";

interface ProductDetailDto extends ProductDto {
  bom: { componentId: string; quantity: number; component: ProductDto }[];
}

export function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, config, moduleEnabled } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const [imageIndex, setImageIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => api<{ product: ProductDetailDto }>(`/catalog/products/${slug}`),
  });

  if (isLoading) return <PageLoader />;
  if (!data) return null;
  const product = data.product;
  const isPack = product.behavior === "PACK" || product.type === "PACK";
  const isCreditPack = product.type === "CREDIT_PACK";
  const soldOut = product.stockAvailable <= 0 && !isCreditPack;
  const shopReviewsEnabled = Boolean(config?.shopReviewsEnabled);
  const flexxiEligibleProduct =
    moduleEnabled("ecommerce") && isFlexxiShopProduct(product);
  const creditPackImage = creditPackageImageUrl(productImageSrc(product) ?? undefined);
  const gallery = isCreditPack
    ? product.displayImages?.length || product.images?.length
      ? product.displayImages?.length
        ? product.displayImages
        : product.images
      : [creditPackImage]
    : product.displayImages?.length
      ? product.displayImages
      : product.images;
  const heroImage = gallery[imageIndex] ?? (isCreditPack ? creditPackImage : productImageSrc(product));

  async function addToCart(buyNow = false) {
    if (!user) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      await api("/cart/items", { body: { productId: product.id, quantity } });
      await queryClient.invalidateQueries({ queryKey: ["cart"] });
      if (buyNow) navigate("/cart");
      else toast("success", "Added to cart");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't add to cart");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-w-0 max-w-full space-y-5 px-4 pt-4 lg:px-6">
      <div className="grid gap-5 lg:grid-cols-[minmax(0,420px)_1fr] lg:items-start lg:gap-10">
        <div className="min-w-0 space-y-3 lg:sticky lg:top-20">
          <div className="relative overflow-hidden rounded-3xl border border-white/5">
            {!isCreditPack && (
              <div className="absolute right-3 top-3 z-10">
                <WishlistHeart kind="SHOP" productId={product.id} />
              </div>
            )}
            <ProductImage
              src={heroImage}
              alt={product.name}
              backgroundColor={product.imageBackgroundColor}
              className={`aspect-square w-full ${isCreditPack ? "p-6" : ""}`}
            />
          </div>
          {gallery.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {gallery.map((image, i) => (
                <button
                  key={image}
                  type="button"
                  onClick={() => setImageIndex(i)}
                  className={`h-14 w-14 overflow-hidden rounded-xl border ${i === imageIndex ? "border-brand-pink" : "border-white/10"}`}
                >
                  <img
                    src={image}
                    alt=""
                    className={`h-full w-full object-contain ${product.imageBackgroundColor ? "" : "bg-surface-2"}`}
                    style={
                      product.imageBackgroundColor
                        ? { backgroundColor: product.imageBackgroundColor }
                        : undefined
                    }
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0 space-y-5">
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <h1 className="font-display text-base font-bold leading-snug lg:text-xl">{product.name}</h1>
              {product.type !== "SINGLE" && <Badge color="blue">{product.type.replace("_", " ")}</Badge>}
            </div>
            <div className="flex flex-wrap items-baseline gap-3">
              <ProductPrice price={product.price} compareAtPrice={product.compareAtPrice} size="lg" />
              {config?.creditsEnabled && product.creditPrice != null && (
                <span className="text-xs text-white/50">or {credits(product.creditPrice)}</span>
              )}
            </div>
            {product.categoryName && <p className="text-[11px] text-white/40">{product.categoryName}</p>}
            {shopReviewsEnabled && (product.ratingCount ?? 0) > 0 && (
              <div className="flex items-center gap-2 pt-1">
                <StarRating value={Math.round(product.avgRating ?? 0)} readOnly size={16} />
                <span className="text-xs text-white/50">
                  {(product.avgRating ?? 0).toFixed(1)} · {product.ratingCount} review
                  {(product.ratingCount ?? 0) === 1 ? "" : "s"}
                </span>
              </div>
            )}
            {product.description && (
              <p className="text-xs leading-relaxed text-white/70 lg:text-sm">{product.description}</p>
            )}
            {Object.keys(product.attributes).length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {Object.entries(product.attributes)
                  .filter(([k, v]) => typeof v === "string" && v && k !== "cardKey" && k !== "source")
                  .map(([k, v]) => (
                    <span key={k} className="rounded-full bg-white/5 px-2.5 py-1 text-[10px] text-white/60">
                      {k}: <span className="text-white/90">{String(v)}</span>
                    </span>
                  ))}
              </div>
            )}
          </div>

          {product.type === "BUNDLE" && product.bom.length > 0 && (
            <div className="rounded-2xl border border-white/5 bg-surface p-3.5">
              <p className="mb-2 font-display text-xs font-semibold">What's inside</p>
              <div className="space-y-2">
                {product.bom.map((bom) => (
                  <div key={bom.componentId} className="flex items-center gap-2.5">
                    <ProductImage
                      src={bom.component.displayImages?.[0] ?? bom.component.images[0] ?? null}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-lg"
                    />
                    <p className="flex-1 truncate text-xs text-white/80">{bom.component.name}</p>
                    <span className="text-xs text-white/40">×{bom.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!soldOut ? (
            <div className="space-y-3 lg:max-w-md">
              <div className="flex items-center gap-3">
                <div className="flex items-center rounded-xl bg-white/5">
                  <button className="px-3.5 py-2 text-lg" onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                    −
                  </button>
                  <span className="w-8 text-center font-display text-sm">{quantity}</span>
                  <button
                    className="px-3.5 py-2 text-lg"
                    onClick={() => setQuantity((q) => Math.min(product.stockAvailable, q + 1))}
                  >
                    +
                  </button>
                </div>
                <span className="text-[11px] text-white/40">
                  {product.type === "CREDIT_PACK"
                    ? "Digital item"
                    : isPack
                      ? `${product.pack?.totalRemaining ?? product.stockAvailable} opens left in pool`
                      : `${product.stockAvailable} in stock`}
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" className="flex-1" disabled={busy} onClick={() => addToCart(false)}>
                  Add to cart
                </Button>
                <Button className="flex-1" disabled={busy} onClick={() => addToCart(true)}>
                  Buy now
                </Button>
              </div>
              {flexxiEligibleProduct ? (
                <FlexxiShopPanel productId={product.id} quantity={quantity} />
              ) : null}
            </div>
          ) : (
            <Button variant="ghost" className="w-full lg:max-w-md" disabled>
              Sold out
            </Button>
          )}
        </div>
      </div>

      {shopReviewsEnabled && !isCreditPack && (
        <section className="border-t border-white/5 pt-6">
          <h2 className="mb-3 font-display text-sm font-semibold text-white">Reviews</h2>
          <ProductReviewsPanel productId={product.id} />
        </section>
      )}
    </div>
  );
}
